// frontend/src/components/bossBattles/BossLeagueCard.js
// Card component for displaying individual boss battle league

import React from 'react';
import './BossLeagueCard.css';

/**
 * BossLeagueCard - Displays a boss battle league with join action
 * 
 * @param {Object} league - Boss league data
 * @param {Function} onJoin - Callback when user joins the league
 * @param {Function} onViewDetails - Callback to view league details
 * @param {boolean} isJoined - Whether user has already joined this league
 * @param {boolean} isJoining - Loading state while joining
 */
function BossLeagueCard({ league, onJoin, onViewDetails, isJoined, isJoining }) {
    // VALIDATE REQUIRED PROPS - ERROR LOUDLY IF MISSING
    if (!league) {
        throw new Error('❌ BossLeagueCard: league prop is required');
    }
    
    // Validate truly critical fields (id, boss_name, status)
    const requiredFields = ['id', 'boss_name', 'status'];
    requiredFields.forEach(field => {
        if (league[field] === undefined) {
            throw new Error(`❌ BossLeagueCard: league.${field} is required`);
        }
    });
    
    // Defensive: Provide fallback for max_participants if missing from API
    const maxParticipants = league.max_participants ?? 10;
    const participantCount = league.participant_count ?? 0;
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'RECRUITING':
                return '#4a90e2'; // Blue
            case 'LOCKED':
                return '#e2a34a'; // Orange
            case 'RESOLVED':
            case 'COMPLETED':
                return '#4ae28c'; // Green
            default:
                return '#a0aec0'; // Gray
        }
    };
    
    const getStatusText = (status) => {
        switch (status) {
            case 'RECRUITING':
                return 'Open for Warriors';
            case 'LOCKED':
                return 'Battle Locked';
            case 'RESOLVED':
                return 'Battle Complete';
            case 'COMPLETED':
                return 'Rewards Claimed';
            default:
                return status;
        }
    };
    
    const canJoin = league.status === 'RECRUITING' && !isJoined;
    const spotsRemaining = maxParticipants - participantCount;
    
    return (
        <div className="boss-league-card">
            <div className="boss-card-header">
                <h3 className="boss-name">{league.boss_name}</h3>
                <span 
                    className="boss-status-badge"
                    style={{ backgroundColor: getStatusColor(league.status) }}
                >
                    {getStatusText(league.status)}
                </span>
            </div>
            
            {league.description && (
                <p className="boss-description">{league.description}</p>
            )}
            
            <div className="boss-stats">
                <div className="boss-stat-item">
                    <span className="stat-label">Difficulty:</span>
                    <span className="stat-value difficulty-value">
                        {league.difficulty || 'Normal'}
                    </span>
                </div>
                
                <div className="boss-stat-item">
                    <span className="stat-label">Warriors:</span>
                    <span className="stat-value">
                        {participantCount}/{maxParticipants}
                    </span>
                </div>
                
                {league.boss_health && (
                    <div className="boss-stat-item">
                        <span className="stat-label">Boss Health:</span>
                        <span className="stat-value health-value">
                            {league.boss_health.toLocaleString()}
                        </span>
                    </div>
                )}
            </div>
            
            {canJoin && spotsRemaining <= 3 && spotsRemaining > 0 && (
                <div className="spots-warning">
                    ⚠️ Only {spotsRemaining} spot{spotsRemaining !== 1 ? 's' : ''} remaining!
                </div>
            )}
            
            <div className="boss-card-actions">
                {canJoin && (
                    <button
                        className="join-boss-button"
                        onClick={() => onJoin && onJoin(league)}
                        disabled={isJoining}
                    >
                        {isJoining ? 'Joining...' : '⚔️ Join Battle'}
                    </button>
                )}
                
                {isJoined && league.status === 'RECRUITING' && (
                    <button
                        className="view-details-button joined"
                        onClick={() => onViewDetails && onViewDetails(league)}
                    >
                        ✓ Joined - Setup Loadout
                    </button>
                )}
                
                {(league.status === 'LOCKED' || league.status === 'RESOLVED' || league.status === 'COMPLETED') && (
                    <button
                        className="view-details-button"
                        onClick={() => onViewDetails && onViewDetails(league)}
                    >
                        View Battle Results
                    </button>
                )}
                
                {!canJoin && league.status === 'RECRUITING' && !isJoined && (
                    <div className="full-message">
                        Battle Roster Full
                    </div>
                )}
            </div>
            
            {league.contest_period && (
                <div className="contest-info">
                    Week {league.contest_period.week || 'TBD'} • {league.contest_period.season_year || '2025'}
                </div>
            )}
        </div>
    );
}

export default BossLeagueCard;
